import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CatalogTableCells } from './CatalogTableCells';
import { CatalogTableFilters, FilterFieldConfig } from './CatalogTableFilters';
import { CatalogTableHeaders } from './CatalogTableHeaders';
import * as styles from './CatalogTable.styles';
import type { ColumnId } from './CatalogTable.types';
import { hasActiveFilters } from './CatalogTable.utils';
import { useCatalogTable } from './useCatalogTable';

// ─── Component Props ───────────────────────────────────────────────────────────

export interface CatalogTableProps {
  /** Columns to display, in order */
  columns: ColumnId[];
  /** Search bar placeholder text */
  searchPlaceholder?: string;
  /** Whether clicking a row navigates to track detail */
  enableTrackDetailNavigation?: boolean;
  /** Filter fields to show */
  filterFields?: FilterFieldConfig[];
  /** Whether to show filters at all */
  showFilters?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────────

export const CatalogTable = ({
  columns,
  searchPlaceholder = 'Search by title, artist, or release...',
  enableTrackDetailNavigation = false,
  filterFields: filterFieldConfigs,
  showFilters = true,
}: CatalogTableProps) => {
  const {
    tracks,
    loading,
    error,
    page,
    pageSize,
    total,
    searchQuery,
    filters,
    sortBy,
    sortOrder,
    setPage,
    setPageSize,
    setSearchQuery,
    setFilters,
    handleSort,
    handlePlayTrack,
    isTrackPlaying,
    applyFilter,
  } = useCatalogTable();

  const debouncedSearchQuery = searchQuery; // Already debounced in hook

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={styles.container}>
      {/* Search */}
      <Box sx={styles.searchAndFilters}>
        <Box sx={styles.searchRow}>
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Filters */}
        {showFilters && filterFieldConfigs && filterFieldConfigs.length > 0 && (
          <CatalogTableFilters
            filters={filters}
            onFiltersChange={(newFilters) => {
              setFilters(newFilters);
            }}
            filterFields={filterFieldConfigs}
          />
        )}
      </Box>

      {/* Error */}
      {error && (
        <Paper sx={styles.errorPaper}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {/* Table */}
      {loading ? (
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : (
      <Box sx={styles.tableWrapper}>
        <TableContainer
          component={Paper}
          sx={styles.tableContainer}
        >
          <Table sx={styles.table}>
            <TableHead sx={styles.tableHeadSticky}>
              <TableRow>
                {columns.map((columnId) => (
                  <CatalogTableHeaders
                    key={columnId}
                    columnId={columnId}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tracks.length === 0 && !loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={styles.emptyStateCell}
                  >
                    {hasActiveFilters(filters) || debouncedSearchQuery ? (
                      <Box sx={styles.emptyStateContent}>
                        <Typography variant="body1" color="text.secondary">
                          No tracks found matching your search
                          {hasActiveFilters(filters) ? ' and filters' : ''}.
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        No tracks found
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                tracks.map((track) => (
                  <TableRow
                    key={track.id}
                    hover
                    sx={
                      enableTrackDetailNavigation
                        ? styles.clickableTableRow
                        : styles.tableRow
                    }
                  >
                    {columns.map((columnId) => (
                      <CatalogTableCells
                        key={columnId}
                        columnId={columnId}
                        track={track}
                        isPlaying={isTrackPlaying(track.id) ?? false}
                        onPlayTrack={handlePlayTrack}
                        onApplyFilter={applyFilter}
                      />
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={styles.pagination}
        />
      </Box>
      )}
    </Box>
  );
};
